import type { ICustomControlProcedureAddEditProps } from '../../interfaces/customControlProcedureAddEditProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    HeaderDivider,
    InputRadioGroup,
    InputTextArea,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { BloeddrukAddEdit } from '../../TCOR/v1/BloeddrukAddEdit';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { NewOrEditProcedureProcessContainer } from '../../utils/v1/NewOrEditProcedureProcessContainer';
import { ServiceOrganizationConsumerSelector } from '../../utils/v1/ServiceOrganizationConsumerSelector';

interface IInnerTiecgV1ProcedureAddEditState {
    buttonDisabled?: boolean;
    isProcessing?: boolean;
}

export function TiecgV1ProcedureAddEdit(props: ICustomControlProcedureAddEditProps) {
    return <InnerTiecgV1ProcedureAddEdit {...props} />;
}

class InnerTiecgV1ProcedureAddEdit extends Component<
    ICustomControlProcedureAddEditProps,
    IInnerTiecgV1ProcedureAddEditState
> {
    public render() {
        return (
            <NewOrEditProcedureProcessContainer
                currentProcedure={this.props.currentProcedure}
                isBioGrader={this.props.isBioGrader}
                isFirstItem={this.props.isFirstItem}
                process={this.props.process}
                service={this.props.service}
            >
                {this.addEditForm()}
            </NewOrEditProcedureProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <Form
                onSubmit={async () => null}
                showOverlay={this.state.isProcessing}
            >
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.procedure}
                />

                <ServiceOrganizationConsumerSelector {...this.props} />

                <AssetDropzoneWithActions
                    assets={this.props.procedure.assets}
                    assetsBaseUrl={FilesUrlBase.procedures}
                    headerText={getServiceLabel(
                        this.props.service.code,
                        'procedureAttachment',
                        translateKey('procedure.procedure')
                    )}
                    onChange={async (assets) => await this.props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => await this.setState({ buttonDisabled: isUploading })}
                    processActivityId={this.props.procedure.id}
                    service={this.props.service}
                />

                <BloeddrukAddEdit
                    customControlData={this.props.procedure.customControlData}
                    errors={this.props.errors}
                    handleChange={this.props.handleChangeCustomControl}
                    isRequired
                    validateOne={this.props.validateOneCustomControl}
                />

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.onderzoek_afgebroken')}>
                    <FormLabel isRequired>Onderzoek afgebroken</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            error={this.props.errors.get('customControlData.onderzoek_afgebroken')}
                            name="onderzoek_afgebroken"
                            onChange={async (value) =>
                                await this.props.handleChangeCustomControl({ onderzoek_afgebroken: value })
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('onderzoek_afgebroken')}
                            options={['Nee', 'Ja']}
                            value={this.props.procedure.customControlData.onderzoek_afgebroken}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <FormDivider />
                <HeaderDivider text={translateKey('common.remarks')} />
                <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                    <FormLabel>{translateKey('common.remark')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('remark')}
                            onChange={async (value) => await this.props.handleChange('remark', value)}
                            onDoValidate={async () => await this.props.validateOne('remark')}
                            placeholder={translateKey('common.remarksOrQuestions')}
                            value={this.props.procedure.remark}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <GraderSelector
                    {...this.props}
                    careProviderAndRequesterAndSelectedProvider={{ ...this.props.procedure }}
                    procedurePerformerOrganizationId={this.props.organizationId}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.procedure}
                            isExisting={!!this.props.request.id}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveProcedure('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveProcedure('final');
                            }}
                        />,
                    ]}
                    hideButtons={this.props.isBioGrader}
                />
            </Form>
        );
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure(saveType);
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        const requestId = await this.props.onSaveProcedure(saveType);
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                this.props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.props.onSave(requestId);
    }
}

export function tiecgV1ProcedureInitializeCustomControlData() {
    return {
        onderzoek_afgebroken: null,
        bloeddruk_systolisch: null,
        bloeddruk_diastolisch: null,
    };
}
